import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';

import { ActiveCartFacade, Cart, DeliveryMode, OrderEntry, PromotionLocation } from '@spartacus/cart/base/root';
import {
  Address,
  CostCenter,
  Country,
  PaymentDetails,
  TranslationService,
  UserAddressService,
  UserCostCenterService,
} from '@spartacus/core';
import { Card, ICON_TYPE } from '@spartacus/storefront';
import { Observable, combineLatest } from 'rxjs';
import { filter, map, startWith, switchMap, tap } from 'rxjs/operators';
import {
  CheckoutCostCenterFacade,
  CheckoutDeliveryFacade,
  CheckoutPaymentFacade,
  CheckoutStep,
  CheckoutStepType,
  PaymentTypeFacade,
  checkoutPaymentSteps,
  checkoutShippingSteps,
} from '../../../root';
import { CheckoutStepService } from '../../services/index';

@Component({
  selector: 'cx-review-submit',
  templateUrl: './review-submit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ReviewSubmitComponent implements OnInit {
  iconTypes = ICON_TYPE;
  checkoutStepType = CheckoutStepType;
  promotionLocation: PromotionLocation = PromotionLocation.ActiveCart;

  constructor(
    protected checkoutDeliveryService: CheckoutDeliveryFacade,
    protected checkoutPaymentService: CheckoutPaymentFacade,
    protected userAddressService: UserAddressService,
    protected activeCartService: ActiveCartFacade,
    protected translation: TranslationService,
    protected checkoutStepService: CheckoutStepService,
    protected paymentTypeService: PaymentTypeFacade,
    protected checkoutCostCenterService: CheckoutCostCenterFacade,
    protected userCostCenterService: UserCostCenterService
  ) {}

  ngOnInit(): void {
    this.checkoutDeliveryService.getCollectLocation();
  }

  get cart$(): Observable<Cart> {
    return this.activeCartService.getActive();
  }

  get entries$(): Observable<OrderEntry[]> {
    return this.activeCartService.getEntries();
  }

  get steps$(): Observable<CheckoutStep[]> {
    return this.checkoutStepService.steps$;
  }

  get deliveryAddress$(): Observable<Address> {
    return this.checkoutDeliveryService.getDeliveryAddress();
  }

  get deliveryMode$(): Observable<DeliveryMode | null | undefined> {
    return this.checkoutDeliveryService.getSelectedDeliveryMode().pipe(
      tap((selected: DeliveryMode | null | undefined) => {
        if (selected === null) {
          this.checkoutDeliveryService.loadSupportedDeliveryModes();
        }
      })
    );
  }
  clickAndCollectLocation$(): Observable<string> {
    return this.checkoutDeliveryService.loadCollectLocation();
  }

  get paymentDetails$(): Observable<PaymentDetails> {
    return this.checkoutPaymentService.getPaymentDetails();
  }

  get countryName$(): Observable<string | null | undefined> {
    return this.deliveryAddress$.pipe(
      switchMap((address: Address) => this.userAddressService.getCountry(address?.country?.isocode as string)),
      tap((country: Country | null) => {
        if (country === null) {
          this.userAddressService.loadDeliveryCountries();
        }
      }),
      map((country: Country | null) => country && country.name)
    );
  }

  poNumberCard$: Observable<Card> = combineLatest([
    this.translation.translate('checkoutReview.poNumber'),
    this.translation.translate('checkoutPO.noPoNumber'),
    this.paymentTypeService.getPoNumber().pipe(startWith('')),
  ]).pipe(
    map(([textTitle, noneTextTitle, poNumber]) => {
      return {
        title: textTitle,
        textBold: poNumber ? poNumber : noneTextTitle,
      };
    })
  );

  get paymentType$(): Observable<string | undefined> {
    return this.paymentTypeService.getSelectedPaymentType();
  }

  get isAccountPayment$(): Observable<boolean> {
    return this.paymentTypeService.isAccountPayment();
  }

  get costCenter$(): Observable<CostCenter | undefined> {
    return this.userCostCenterService.getActiveCostCenters().pipe(
      filter((costCenters) => Boolean(costCenters)),
      switchMap((costCenters) => {
        return this.checkoutCostCenterService.getCostCenter().pipe(
          map((code) => {
            return costCenters.find((cc) => cc.code === code);
          })
        );
      })
    );
  }

  getShippingAddressCard(deliveryAddress: Address, countryName: string): Observable<Card> {
    return combineLatest([this.translation.translate('addressCard.shipTo')]).pipe(
      map(([textTitle]) => {
        if (!countryName) {
          countryName = deliveryAddress?.country?.isocode as string;
        }

        let region = '';
        if (deliveryAddress && deliveryAddress.region && deliveryAddress.region.isocode) {
          region = deliveryAddress.region.isocode + ', ';
        }

        return {
          title: textTitle,
          textBold: deliveryAddress.firstName + ' ' + deliveryAddress.lastName,
          text: [
            deliveryAddress.line1 + ' ' + deliveryAddress.line2,
            deliveryAddress.postalCode + ' ' + deliveryAddress.town + ' ' + region,
            countryName,
            deliveryAddress.phone,
          ],
        } as Card;
      })
    );
  }

  getCostCenterCard(costCenter?: CostCenter): Observable<Card> {
    return combineLatest([this.translation.translate('checkoutPO.costCenter')]).pipe(
      map(([textTitle]) => {
        return {
          title: textTitle,
          textBold: costCenter?.name,
          text: ['(' + costCenter?.unit?.name + ')'],
        };
      })
    );
  }

  getDeliveryModeCard(
    isShipping: boolean,
    deliveryMode?: DeliveryMode | null,
    clickAndCollectLocation?: string | null
  ): Observable<Card> {
    return combineLatest([
      this.translation.translate('checkoutShipping.shippingMethod'),
      this.translation.translate('addressCard.shipTo'),
      this.translation.translate('checkoutShipping.pickupLocation'),
    ]).pipe(
      map(([textTitle, text2, pickupLocation]) => {
        return {
          title: isShipping ? text2 : textTitle,
          textBold: deliveryMode?.name,
          text: [
            !clickAndCollectLocation ? deliveryMode?.description : '',
            !clickAndCollectLocation
              ? deliveryMode?.deliveryCost?.formattedValue
                ? deliveryMode.deliveryCost?.formattedValue
                : ''
              : pickupLocation + ' ' + clickAndCollectLocation,
          ],
        } as Card;
      })
    );
  }

  getPaymentMethodCard(paymentDetails: PaymentDetails): Observable<Card> {
    return combineLatest([
      this.translation.translate('paymentForm.payment'),
      this.translation.translate('paymentForm.billingAddress'),
    ]).pipe(
      map(([textTitle, billingAddress]) => {
        const region = paymentDetails.billingAddress?.region?.isocode
          ? paymentDetails.billingAddress?.region?.isocode + ', '
          : '';
        return {
          title: textTitle,
          textBold: billingAddress + ':',
          text: [
            paymentDetails.billingAddress?.firstName + ' ' + paymentDetails.billingAddress?.lastName,
            paymentDetails.billingAddress?.line1 + ' ' + paymentDetails.billingAddress?.line2,
            paymentDetails.billingAddress?.postalCode + ' ' + paymentDetails.billingAddress?.town + ' ' + region,
            paymentDetails.billingAddress?.country?.name,
          ],
        } as Card;
      })
    );
  }

  getPaymentTypeCard(paymentType?: string): Observable<Card> {
    return combineLatest([
      this.translation.translate('checkoutProgress.methodOfPayment'),
      this.translation.translate('paymentTypes.paymentType_' + paymentType),
    ]).pipe(
      map(([textTitle, paymentTypeTranslation]) => {
        return {
          title: textTitle,
          textBold: paymentTypeTranslation,
        };
      })
    );
  }

  getCheckoutStepUrl(stepType: CheckoutStepType): string | undefined {
    const step = this.checkoutStepService.getCheckoutStep(stepType);
    return step && step.routeName;
  }

  shippingSteps(steps: CheckoutStep[]): CheckoutStep[] {
    return steps.filter((step) => checkoutShippingSteps.includes(step.type[0]));
  }

  paymentSteps(steps: CheckoutStep[]): CheckoutStep[] {
    return steps.filter((step) => checkoutPaymentSteps.includes(step.type[0]));
  }
}
